import React, { useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useLoadingStates } from '../../../hooks/useLoadingStates';
import { AuthMode } from './AuthModal';
import EmailInput from '../EmailInput';
import LoadingButton from '../LoadingButton';
import styles from './AuthModal.module.css';

interface PasswordContentProps {
  onSwitchMode: (mode: AuthMode) => void;
  onSuccess: () => void;
}

const PasswordContent: React.FC<PasswordContentProps> = ({ onSwitchMode, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Loading states
  const { withLoading, isLoading } = useLoadingStates();
  const isResetLoading = isLoading('reset');

  // Email validation
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Handle password reset
  const handlePasswordReset = async () => {
    // Clear previous messages
    setError('');
    setSuccessMessage('');

    // Validate email
    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    try {
      await withLoading('reset', async () => {
        const { getAuth } = await import('@/firebaseLazy');
        const auth = await getAuth();
        await sendPasswordResetEmail(auth, email);
        setSuccessMessage('Password reset email sent successfully! Check your inbox and click the link to reset your password.');
        setError('');
      });
    } catch (error: any) {
      console.error('Password reset error:', error);
      
      // Handle specific Firebase errors
      switch (error.code) {
        case 'auth/user-not-found':
          setError('No account found with this email address.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        case 'auth/too-many-requests':
          setError('Too many reset attempts. Please try again later.');
          break;
        default:
          setError('The email address is wrong or does not exist.');
      }
      setSuccessMessage('');
    }
  };

  // Handle input change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Clear errors when user types
    if (error) {
      setError('');
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handlePasswordReset();
    }
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.mainContent}>
        {/* Header */}
        <div className={styles.authHeader}>
        <h1 className={styles.authTitle}>Reset your password</h1>
        <p className={styles.authSubtitle}>
          Enter your email to receive a reset link.
        </p>
      </div>

      {/* Email Form */}
      <form onSubmit={(e) => { e.preventDefault(); handlePasswordReset(); }}>
        <div style={{ marginBottom: '1rem' }}>
          <EmailInput
            id="reset-email-input"
            placeholder="Email address"
            value={email}
            onChange={handleEmailChange}
            onKeyDown={handleKeyDown}
            hasError={!!error}
            className={styles.formInput}
            autoComplete="email"
            autoFocus
          />
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className={styles.successMessage} style={{ marginBottom: '1rem' }}>
            {successMessage}
          </div>
        )}

        {/* Submit Button */}
        {!successMessage && (
          <LoadingButton
            variant="primary"
            onClick={handlePasswordReset}
            loading={isResetLoading}
            loadingText="Sending reset email..."
            className={styles.primaryButton}
            style={{ width: '100%', marginBottom: '1.5rem' }}
            disabled={!email}
          >
            Send Reset Email
          </LoadingButton>
        )}

        {/* Success Action */}
        {successMessage && (
          <button
            type="button"
            onClick={() => onSwitchMode('login')}
            className={styles.primaryButton}
            style={{ width: '100%', marginBottom: '1.5rem' }}
          >
            Back to Login
          </button>
        )}
      </form>

        {/* Footer */}
        <div className={styles.formFooter}>
          <p className={styles.switchModeText}>
            Remember your password?{' '}
            <span 
              className={styles.switchModeLink} 
              onClick={() => onSwitchMode('login')}
            >
              Sign in
            </span>
          </p>
        </div>
      </div>

      {/* Terms - Pushed to Bottom */}
      <div className={styles.termsText}>
        By continuing, you agree to our{' '}
        <a href="/terms-of-service" target="_blank" rel="noopener">Terms of Use</a>.
        Read our{' '}
        <a href="/privacy-policy" target="_blank" rel="noopener">Privacy Policy</a>.
      </div>
    </div>
  );
};

export default PasswordContent;